import { CascoPackage, ContractorType } from '@insurance/api/requests'
import { ConditionalText, DateTimeCell, Text } from '@shared/components'
import { CAR_CURRENCY, DEFAULT_CURRENCY } from '@shared/constants'
import { getFormatCurrency, getFullName, YEAR_FORMAT } from '@shared/utils'
import { Divider, Flex, Form } from 'antd'
import { DescriptionsProps } from 'antd/lib'
import { useTranslation } from 'react-i18next'

import { DetailsWrapper } from '../DetailsWrapper'
import { useCascoInsuranceValues } from './CascoInsuranceContext'
import { useStyles } from './styles'
import { CascoFormValues } from './type'
import { enableEngineFields, enableFranchise, formProps } from './utils'

type ExtraDescriptions = (Required<DescriptionsProps>['items'][number] & {
  hidden?: boolean
})[]

export const Details = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { isEditable, order } = useCascoInsuranceValues()

  const {
    priceKey,
    insuredAmountProps,
    insuredMiniAmountProps,
    contractorProps,
    insurancePackageProps,
    validFromProps,
    vehicleRootProps,
    vehicleCategoryProps,
    vehicleYearProps,
    vehicleIsFromChina,
    vehicleIsPremium,
    vehicleColorProps,
    vehicleFuelProps,
    vehicleTransmissionProps,
    vehicleMileAgeProps,
    franchiseProps,
    driverAgeProps,
    driverExperienceProps,
    territoryProps,
    parkingModeProps,
    annualBonusProps,
    vehicleQuantityProps,
    claimLastYearProps,
    officialDealerRepairProps,
    periodProps,
    installmentsProps,
    additionalEquipmentDescriptionProps,
    additionalEquipmentAmountProps,
    costProps,
    vehicleEngineCapacityProps,
    vehicleSeatsProps,
    vehicleWeightProps,
    effectiveDateProps
  } = formProps(t)

  const price = Form.useWatch(priceKey.name, { preserve: true })
  const insuredCommonAmount = Form.useWatch(insuredAmountProps.name, {
    preserve: true
  })
  const cost = Form.useWatch(costProps.name, {
    preserve: true
  })
  const insuredMiniAmount = Form.useWatch(insuredMiniAmountProps.name, {
    preserve: true
  })
  const contractor = Form.useWatch<CascoFormValues['contractor']>(
    contractorProps.name,
    {
      preserve: true
    }
  )
  const validFrom = Form.useWatch(validFromProps.name, {
    preserve: true
  })
  const insurancePackage = Form.useWatch(insurancePackageProps.name, {
    preserve: true
  })
  const vehicle = Form.useWatch<CascoFormValues['vehicle']>(
    vehicleRootProps.name,
    {
      preserve: true
    }
  )
  const franchise = Form.useWatch(franchiseProps.name, {
    preserve: true
  })
  const driverAge = Form.useWatch(driverAgeProps.name, {
    preserve: true
  })
  const driverExperience = Form.useWatch(driverExperienceProps.name, {
    preserve: true
  })
  const territory = Form.useWatch(territoryProps.name, {
    preserve: true
  })
  const parkingMode = Form.useWatch(parkingModeProps.name, {
    preserve: true
  })
  const annualBonus = Form.useWatch(annualBonusProps.name, {
    preserve: true
  })
  const vehicleQuantity = Form.useWatch(vehicleQuantityProps.name, {
    preserve: true
  })
  const claimLastYear = Form.useWatch(claimLastYearProps.name, {
    preserve: true
  })
  const officialDealerRepair = Form.useWatch(officialDealerRepairProps.name, {
    preserve: true
  })
  const period = Form.useWatch(periodProps.name, {
    preserve: true
  })
  const installments = Form.useWatch(installmentsProps.name, {
    preserve: true
  })
  const additionalEquipmentAmount = Form.useWatch(
    additionalEquipmentAmountProps.name,
    {
      preserve: true
    }
  )
  const additionalEquipmentDescription = Form.useWatch(
    additionalEquipmentDescriptionProps.name,
    {
      preserve: true
    }
  )
  const vehicleWeight = Form.useWatch(vehicleWeightProps.name, {
    preserve: true
  })
  const vehicleSeats = Form.useWatch(vehicleSeatsProps.name, {
    preserve: true
  })
  const vehicleEngineCapacity = Form.useWatch(vehicleEngineCapacityProps.name, {
    preserve: true
  })
  const effectiveDate = Form.useWatch(effectiveDateProps.name, {
    preserve: true
  })

  const isLegalEntity = contractor?.content_type === ContractorType.LEGALENTITY

  const isComfortPackage = insurancePackage === CascoPackage.COMFORT
  const isMiniPackage = insurancePackage === CascoPackage.MINI

  const insuredAmount = !isMiniPackage ? insuredCommonAmount : insuredMiniAmount

  const showEngineFields = enableEngineFields(vehicle?.casco_category)

  const mainItems: ExtraDescriptions = [
    {
      label: contractorProps.label,
      children: (
        <ConditionalText
          condition={!!contractor?.content_type}
          className={styles.text}
        >
          {t(contractor?.content_type)}
        </ConditionalText>
      )
    },
    {
      label: t('company_name'),
      children: (
        <ConditionalText
          condition={!!contractor?.legal_entity?.name}
          className={styles.text}
        >
          {contractor?.legal_entity?.name}
        </ConditionalText>
      ),
      hidden: !isLegalEntity
    },
    {
      label: t('client_name'),
      children: (
        <ConditionalText
          condition={!!getFullName(contractor?.person)}
          className={styles.text}
        >
          {getFullName(contractor?.person)}
        </ConditionalText>
      ),
      hidden: isLegalEntity
    },
    {
      label: costProps.label,
      children: getFormatCurrency({
        amount: cost,
        currency: CAR_CURRENCY
      }),
      hidden: isMiniPackage
    },
    {
      label: !isMiniPackage
        ? insuredAmountProps.label
        : insuredMiniAmountProps.label,
      children: getFormatCurrency({
        amount: insuredAmount,
        currency: !isMiniPackage ? CAR_CURRENCY : DEFAULT_CURRENCY
      })
    },
    {
      label: validFromProps.label,
      children: (
        <DateTimeCell
          date={validFrom}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      ),
      hidden: isEditable
    },
    {
      label: effectiveDateProps.label,
      children: (
        <DateTimeCell
          dateProps={{ className: styles.date }}
          date={effectiveDate}
          hiddenTime
        />
      ),
      hidden: !isEditable
    },
    {
      label: insurancePackageProps.label,
      children: (
        <ConditionalText condition={!!insurancePackage} className={styles.text}>
          {t(insurancePackage)}
        </ConditionalText>
      )
    },

    // Vehicle information ----------->
    {
      label: vehicleCategoryProps.label,
      children: (
        <ConditionalText
          condition={!!vehicle?.casco_category}
          className={styles.text}
        >
          {t(vehicle?.casco_category || '')}
        </ConditionalText>
      )
    },
    {
      label: vehicleRootProps.label,
      children: (
        <ConditionalText
          condition={!![vehicle?.brand, vehicle?.model].some(Boolean)}
          className={styles.text}
        >
          {[vehicle?.brand, vehicle?.model].filter(Boolean).join(' ')}
        </ConditionalText>
      )
    },
    {
      label: vehicleYearProps.label,
      children: (
        <DateTimeCell
          date={vehicle?.year}
          dateFormat={YEAR_FORMAT}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      )
    },
    {
      label: vehicleColorProps.label,
      children: (
        <ConditionalText condition={!!vehicle?.color} className={styles.text}>
          {vehicle?.color}
        </ConditionalText>
      )
    },
    {
      label: vehicleFuelProps.label,
      children: (
        <ConditionalText
          condition={!!vehicle?.fuel_type}
          className={styles.text}
        >
          {t(vehicle?.fuel_type || '')}
        </ConditionalText>
      ),
      hidden: !showEngineFields
    },
    {
      label: vehicleTransmissionProps.label,
      children: (
        <ConditionalText
          condition={!!vehicle?.transmission_type}
          className={styles.text}
        >
          {t(vehicle?.transmission_type || '')}
        </ConditionalText>
      ),
      hidden: !showEngineFields
    },
    {
      label: vehicleMileAgeProps.label,
      children: (
        <ConditionalText condition={!!vehicle?.mileage} className={styles.text}>
          {vehicle?.mileage}
        </ConditionalText>
      ),
      hidden: !showEngineFields
    },
    {
      label: vehicleSeatsProps.label,
      children: vehicleSeats,
      hidden: !vehicleSeats || !showEngineFields
    },
    {
      label: vehicleEngineCapacityProps.label,
      children: [vehicleEngineCapacity, t('cm3')].join(' '),
      hidden: !vehicleEngineCapacity || !showEngineFields
    },
    {
      label: vehicleWeightProps.label,
      children: [vehicleWeight, t('kg')].join(' '),
      hidden: !vehicleWeight
    },
    {
      label: vehicleIsPremium.label,
      children: vehicle?.brand_is_premium ? t('yes') : t('no')
    },
    {
      label: vehicleIsFromChina.label,
      children: vehicle?.brand_is_china ? t('yes') : t('no')
    },
    {
      label: franchiseProps.label,
      children: (
        <ConditionalText condition={!!franchise} className={styles.text}>
          {t(franchise)}
        </ConditionalText>
      ),
      hidden: !enableFranchise(vehicle?.casco_category, insurancePackage)
    }
  ]

  const correctionsItems: ExtraDescriptions = [
    {
      label: driverAgeProps.label,
      children: (
        <ConditionalText condition={!!driverAge} className={styles.text}>
          {t(driverAge)}
        </ConditionalText>
      ),
      hidden: isMiniPackage
    },
    {
      label: driverExperienceProps.label,
      children: (
        <ConditionalText condition={!!driverExperience} className={styles.text}>
          {t(driverExperience)}
        </ConditionalText>
      ),
      hidden: isMiniPackage
    },
    {
      label: territoryProps.label,
      children: (
        <ConditionalText condition={!!territory} className={styles.text}>
          {t(territory)}
        </ConditionalText>
      )
    },
    {
      label: parkingModeProps.label,
      children: (
        <ConditionalText condition={!!parkingMode} className={styles.text}>
          {t(parkingMode)}
        </ConditionalText>
      ),
      hidden: isMiniPackage
    },
    {
      label: annualBonusProps.label,
      children: (
        <ConditionalText condition={!!annualBonus} className={styles.text}>
          {annualBonus} %
        </ConditionalText>
      ),
      hidden: isMiniPackage
    },
    {
      label: vehicleQuantityProps.label,
      children: (
        <ConditionalText condition={!!vehicleQuantity} className={styles.text}>
          {vehicleQuantity}
        </ConditionalText>
      ),
      hidden: isMiniPackage
    },
    {
      label: claimLastYearProps.label,
      children: claimLastYear ? t('yes') : t('no'),
      hidden: isMiniPackage
    },
    {
      label: officialDealerRepairProps.label,
      children: officialDealerRepair ? t('yes') : t('no'),
      hidden: isMiniPackage
    },
    {
      label: t('replacement_vehicle'),
      children: t('yes'),
      hidden: !isComfortPackage
    },
    {
      label: periodProps.label,
      children: (
        <ConditionalText condition={!!period} className={styles.text}>
          {t(`m${period}`)}
        </ConditionalText>
      )
    },
    {
      label: installmentsProps.label,
      children: (
        <ConditionalText condition={!!installments} className={styles.text}>
          {t(`installment_${installments}`)}
        </ConditionalText>
      ),
      hidden: isMiniPackage
    },
    {
      label: additionalEquipmentAmountProps.label,
      children: (
        <ConditionalText
          condition={!!additionalEquipmentAmount}
          className={styles.text}
        >
          {getFormatCurrency({
            amount: additionalEquipmentAmount,
            currency: CAR_CURRENCY
          })}
        </ConditionalText>
      ),
      hidden: !additionalEquipmentDescription
    }
  ]

  const items = [...mainItems, ...correctionsItems].filter(
    ({ hidden }) => !hidden
  )

  const orderPrice = {
    amount: order?.full_price ? Number(order.full_price) : 0,
    currency: order?.currency
  }

  const isMoreThanOrderPrice = price?.amount - orderPrice?.amount > 0

  return (
    <DetailsWrapper items={items}>
      {price && !isEditable && (
        <>
          <Flex align="center" justify="space-between">
            <Text strong size={18}>
              {t('total_to_be_paid')}
            </Text>
            <Text strong size={18} className="text-wrap-nowrap">
              {getFormatCurrency(price)}
            </Text>
          </Flex>

          <Divider />
        </>
      )}

      {isEditable && (
        <>
          {orderPrice && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('policy_amount_paid')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(orderPrice)}
              </Text>
            </Flex>
          )}
          {price && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('new_amount')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(price)}
              </Text>
            </Flex>
          )}
          {orderPrice && price && (
            <Flex align="center" justify="space-between">
              <Text
                strong
                size={22}
                type={isMoreThanOrderPrice ? 'success' : 'danger'}
              >
                {isMoreThanOrderPrice
                  ? t('amount_to_be_paid')
                  : t('amount_to_be_returned')}
              </Text>
              <Text
                strong
                size={22}
                type={isMoreThanOrderPrice ? 'success' : 'danger'}
                className="text-wrap-nowrap"
              >
                {getFormatCurrency({
                  amount: price.amount - orderPrice.amount,
                  currency: price.currency
                })}
              </Text>
            </Flex>
          )}

          <Divider />
        </>
      )}
    </DetailsWrapper>
  )
}
